// =============================================================================
// Rdzeń stanu edytora układów — operacje na drzewie węzłów i historia.
//
// TU NIE MA REACTA I NIE MA PIKSELI. Milimetr jest jednostką modelu (tak jak
// w gramatyce z E0); piksel pojawia się dopiero w `Plotno.tsx`, przy mnożeniu
// przez skalę. Dzięki temu cofnięcie, przesunięcie i zapis są testowalne bez
// przeglądarki, a zmiana powiększenia płótna nie może ruszyć zapisanego układu.
//
// DRZEWO, NIE LISTA. `repeat` zawiera węzły potomne (`zrodlo.wiersze` dla
// wariantu literalnego, `zrodlo.wiersz` dla listy), więc każda operacja musi
// schodzić w głąb. Płaska pętla po `layout.nodes` gubi logo w powtórzeniu —
// dokładnie ten błąd wyszedł już raz w bramce obrazów w print-core.
// =============================================================================

import type { LayoutNode, PrintLayout, Token } from '@insolitum/print-templates';
// Podścieżka `/uklad`, nie korzeń: korzeń ciągnie `render.ts` z `node:crypto`
// i nie wchodzi do bundla przeglądarki. To wejście jest browser-safe z bramki.
import {
  NAZWA_KSZTALTU_TERAZ,
  obrysWezla,
  obszarZadrukuMm,
  ptNaMm,
  type KsztaltTeraz,
} from '@insolitum/print-templates/uklad';

/** Ile kroków historii trzymamy. Powyżej — najstarsze wypadają. */
const GLEBOKOSC_HISTORII = 60;

/**
 * Nowy identyfikator węzła.
 *
 * `crypto.randomUUID` bywa nieobecne poza bezpiecznym kontekstem (http na
 * adresie IP w hali), a wtedy rzuca — dlatego jest zapasowa ścieżka. Kolizja
 * przy losowaniu z 36^10 jest tu bez znaczenia: identyfikator żyje w obrębie
 * jednego układu, w którym węzłów są dziesiątki, a nie miliony.
 */
export function nowyId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `w-${Math.random().toString(36).slice(2, 12)}`;
}

/** Rekurencyjne mapowanie węzłów — schodzi w `repeat`. */
export function mapujWezly(
  nodes: LayoutNode[],
  fn: (n: LayoutNode) => LayoutNode | null
): LayoutNode[] {
  const out: LayoutNode[] = [];
  for (const node of nodes) {
    const zmieniony = fn(node);
    if (zmieniony === null) continue;
    out.push(zejdz(zmieniony, fn));
  }
  return out;
}

/** Zastosowanie `fn` do potomków węzła `repeat`; inne rodzaje zwraca bez zmian. */
function zejdz(node: LayoutNode, fn: (n: LayoutNode) => LayoutNode | null): LayoutNode {
  if (node.kind !== 'repeat') return node;
  switch (node.zrodlo.rodzaj) {
    case 'literalne':
      return {
        ...node,
        zrodlo: {
          ...node.zrodlo,
          wiersze: node.zrodlo.wiersze.map((wiersz) => mapujWezly(wiersz, fn)),
        },
      };
    case 'lista':
      return {
        ...node,
        zrodlo: { ...node.zrodlo, wiersz: mapujWezly(node.zrodlo.wiersz, fn) },
      };
    case 'zawijanie':
      // Zawijanie nie ma węzłów potomnych — tekst łamie interpreter.
      return node;
  }
}

/** Wszystkie węzły układu, z zachowaniem kolejności rysowania. */
export function wszystkieWezly(layout: PrintLayout): LayoutNode[] {
  const out: LayoutNode[] = [];
  mapujWezly(layout.nodes, (n) => {
    out.push(n);
    return n;
  });
  return out;
}

/** Węzeł po identyfikatorze — także zagnieżdżony. */
export function znajdzWezel(layout: PrintLayout, id: string): LayoutNode | null {
  return wszystkieWezly(layout).find((n) => n.id === id) ?? null;
}

/** Podmiana jednego węzła (niezmiennie, w głąb drzewa). */
export function zmienWezel(
  layout: PrintLayout,
  id: string,
  fn: (n: LayoutNode) => LayoutNode
): PrintLayout {
  return { ...layout, nodes: mapujWezly(layout.nodes, (n) => (n.id === id ? fn(n) : n)) };
}

/** Usunięcie węzła — także zagnieżdżonego. */
export function usunWezel(layout: PrintLayout, id: string): PrintLayout {
  return { ...layout, nodes: mapujWezly(layout.nodes, (n) => (n.id === id ? null : n)) };
}

/**
 * Czy węzeł ma własne współrzędne, które da się przeciągać.
 *
 * `repeat` ma tylko `yMm` (wiersze rozkłada `stepMm`), więc przeciąganie w osi
 * X nie ma dla niego znaczenia — a `line` ma DWA końce i przesuwa się oba
 * naraz, żeby nie zmieniać długości przy przeciąganiu za środek.
 */
export function przesunWezel(node: LayoutNode, dxMm: number, dyMm: number): LayoutNode {
  if (node.zablokowany) return node;
  const zaokr = (v: number) => Math.round(v * 100) / 100;
  if (node.kind === 'repeat') return { ...node, yMm: zaokr(node.yMm + dyMm) };
  if (node.kind === 'line') {
    return {
      ...node,
      xMm: zaokr(node.xMm + dxMm),
      yMm: zaokr(node.yMm + dyMm),
      x2Mm: zaokr(node.x2Mm + dxMm),
      y2Mm: zaokr(node.y2Mm + dyMm),
    };
  }
  return { ...node, xMm: zaokr(node.xMm + dxMm), yMm: zaokr(node.yMm + dyMm) };
}

/** Przyciąganie do siatki — zwraca przyrost skorygowany, nie pozycję. */
export function przyciagnij(wartosc: number, siatkaMm: number, wlaczone: boolean): number {
  if (!wlaczone || siatkaMm <= 0) return wartosc;
  return Math.round(wartosc / siatkaMm) * siatkaMm;
}

// ── Historia ─────────────────────────────────────────────────────────────────

export interface Historia {
  /** Stany od najstarszego; `wskaznik` pokazuje bieżący. */
  stany: PrintLayout[];
  wskaznik: number;
}

export function nowaHistoria(poczatkowy: PrintLayout): Historia {
  return { stany: [poczatkowy], wskaznik: 0 };
}

/**
 * Dopisanie stanu.
 *
 * Wszystko po `wskazniku` przepada — to jest właśnie znaczenie „cofnąłem się
 * i zrobiłem coś innego". Bez tego cięcia ponowienie po nowej zmianie
 * przywracałoby gałąź, której użytkownik już nie widzi.
 */
export function dopiszHistorie(h: Historia, stan: PrintLayout): Historia {
  const stany = [...h.stany.slice(0, h.wskaznik + 1), stan];
  if (stany.length > GLEBOKOSC_HISTORII) stany.shift();
  return { stany, wskaznik: stany.length - 1 };
}

export function mozeCofnac(h: Historia): boolean {
  return h.wskaznik > 0;
}

export function mozePonowic(h: Historia): boolean {
  return h.wskaznik < h.stany.length - 1;
}

export function cofnij(h: Historia): Historia {
  return mozeCofnac(h) ? { ...h, wskaznik: h.wskaznik - 1 } : h;
}

export function ponow(h: Historia): Historia {
  return mozePonowic(h) ? { ...h, wskaznik: h.wskaznik + 1 } : h;
}

export function biezacy(h: Historia): PrintLayout {
  return h.stany[h.wskaznik];
}

// ── Nowe węzły ───────────────────────────────────────────────────────────────

/**
 * Świeży węzeł danego rodzaju, wstawiany w podane miejsce.
 *
 * Wartości domyślne są CELOWO widoczne: tekst „Nowy tekst" i ramka 20×10 mm
 * mają się rzucać w oczy na płótnie. Węzeł o zerowym rozmiarze albo z pustym
 * wyrażeniem byłby niewidoczny, a użytkownik zobaczyłby, że kliknięcie
 * „Dodaj" nic nie zrobiło.
 */
export function nowyWezel(kind: LayoutNode['kind'], xMm: number, yMm: number): LayoutNode {
  const meta = { id: nowyId() };
  switch (kind) {
    case 'text':
      return { ...meta, kind: 'text', xMm, yMm, tekst: [{ lit: 'Nowy tekst' }], sizePt: 10 };
    case 'barcode':
      return {
        ...meta,
        kind: 'barcode',
        xMm,
        yMm,
        dane: [{ lit: '0000000000' }],
        symbology: 'code128',
        heightMm: 12,
        showText: true,
      };
    case 'qr':
      return { ...meta, kind: 'qr', xMm, yMm, dane: [{ lit: 'https://insolitum.ai' }], sizeMm: 20 };
    case 'line':
      return { ...meta, kind: 'line', xMm, yMm, x2Mm: xMm + 30, y2Mm: yMm, thicknessMm: 0.3 };
    case 'box':
      return { ...meta, kind: 'box', xMm, yMm, widthMm: 20, heightMm: 10, thicknessMm: 0.3 };
    case 'image':
      return {
        ...meta,
        kind: 'image',
        xMm,
        yMm,
        widthMm: 20,
        heightMm: 20,
        zrodlo: { rodzaj: 'logo_organizacji' },
        alt: 'Logo',
      };
    case 'repeat':
      return {
        ...meta,
        kind: 'repeat',
        yMm,
        stepMm: 5,
        zrodlo: { rodzaj: 'literalne', wiersze: [] },
      };
  }
}

/**
 * Przesunięcie PRZYCIĘTE do obszaru zadruku.
 *
 * Bez tego edytor pozwalał doprowadzić układ do stanu, którego sam nie umiał
 * zapisać: blok wyciągnięty myszą za krawędź zapalał `poza_mediami`, autozapis
 * odbijał się 422-ką i praca stawała, a jedynym śladem była czerwona linijka
 * w stopce. Kursor wolno wyprowadzić za etykietę — blokowi nie wolno za nią
 * pojechać.
 *
 * Reguła jest DOKŁADNIE tą, którą mierzy `naruszeniaMediow`, i to jest cały
 * powód, dla którego liczy ją `obrysWezla`, a nie własne `xMm + widthMm`:
 * lewy górny róg trzymamy zawsze, prawą i dolną krawędź tylko wtedy, gdy obrys
 * jest PEWNY. Tekst bez `maxWidthMm` ma szerokość znaną dopiero przeglądarce,
 * więc przycinanie go prawą krawędzią odsuwałoby od niej bloki, których
 * walidator nigdy nie zaczepia — i to użytkownik odczytałby jako zacinanie się
 * myszy, nie jako pomoc.
 *
 * Blok, który JUŻ jest poza obszarem (szkic sprzed tej poprawki), pierwszym
 * chwytem wraca do środka — dlatego dolna granica bywa większa od górnej
 * i kolejność `min`/`max` nie jest tu ozdobna.
 */
export function przycinajDoObszaru(
  node: LayoutNode,
  media: PrintLayout['media'],
  dx: number,
  dy: number
): { dx: number; dy: number } {
  const o = obrysWezla(node);
  const { widthMm, heightMm } = obszarZadrukuMm(media);

  const przytnij = (
    delta: number,
    poczatek: number,
    rozmiar: number,
    granica: number,
    szacowany: boolean
  ): number => {
    const dolna = -poczatek;
    const gorna = szacowany ? granica - poczatek : granica - poczatek - rozmiar;
    return Math.min(Math.max(delta, dolna), Math.max(dolna, gorna));
  };

  return {
    dx: przytnij(dx, o.xMm, o.widthMm, widthMm, o.szacowanaSzerokosc),
    dy: przytnij(dy, o.yMm, o.heightMm, heightMm, o.szacowanaWysokosc),
  };
}

/** Granice powiększenia płótna w px na milimetr. */
export const SKALA_MIN = 0.5;
export const SKALA_MAX = 24;

/** Skala odpowiadająca wydrukowi 1:1 na ekranie (96 px na cal / 25,4 mm). */
export const SKALA_1_DO_1 = 96 / 25.4;

/**
 * Powiększenie, przy którym cała etykieta mieści się w widoku.
 *
 * Domyślne „3 px na milimetr" było wartością wpisaną na sztywno i to ono
 * robiło z etykiety 80×50 mm znaczek 300×190 px pośrodku okna — obok pustego
 * panelu właściwości szerokiego na 280 px. Przy 8 pt tekst miał na ekranie
 * 8,5 px wysokości, czyli był nieczytelny na płótnie, którego jedynym
 * zadaniem jest pokazać, gdzie ten tekst leży.
 *
 * Liczymy więc z faktycznego rozmiaru kontenera i robimy to TUTAJ, a nie
 * w komponencie, żeby dało się to sprawdzić bez przeglądarki. `margines` to
 * oddech wokół etykiety — bez niego cień i krawędź płótna dotykają ścianek
 * i nie widać, gdzie kończy się nośnik.
 */
export function skalaDopasowania(
  kontenerPx: { szer: number; wys: number },
  mediaMm: { szer: number; wys: number },
  marginesPx = 48
): number {
  const dostepneX = kontenerPx.szer - marginesPx;
  const dostepneY = kontenerPx.wys - marginesPx;
  // Kontener bywa zerowy w pierwszej klatce (przed pomiarem) i przy zwiniętym
  // oknie. Skala 0 dałaby płótno o zerowych wymiarach, którego nie da się
  // kliknąć, żeby cokolwiek naprawić — dlatego dół zakresu, nie zero.
  if (dostepneX <= 0 || dostepneY <= 0 || mediaMm.szer <= 0 || mediaMm.wys <= 0) return SKALA_MIN;
  const skala = Math.min(dostepneX / mediaMm.szer, dostepneY / mediaMm.wys);
  return Math.min(SKALA_MAX, Math.max(SKALA_MIN, Math.round(skala * 100) / 100));
}

/** Zmienna wzoru w kształcie, jaki panel dostaje z `GET /api/print/templates`. */
export interface ZmiennaKatalogu {
  name: string;
  kind?: string;
  example?: unknown;
  optional?: boolean;
}

/** Ile kolumn tabeli wstawiamy najwyżej — dalej etykieta robi się nieczytelna. */
const KOLUMN_MAX = 4;

/**
 * Nazwy kolumn dla tabeli — z PRZYKŁADU zmiennej listowej, nie ze zgadywania.
 *
 * `PrintVariable.example` katalogu jest realistyczną wartością (to wymóg
 * katalogu, nie konwencja), więc dla zmiennej `kind: 'list'` klucze pierwszej
 * pozycji SĄ nazwami pól, które przyjdą w prawdziwym zadaniu druku. Zgadywanie
 * ich („name", „qty") dałoby tabelę, która wygląda dobrze w edytorze i drukuje
 * puste komórki — a pusta komórka na etykiecie nie krzyczy.
 */
export function kolumnyListy(zmienna: ZmiennaKatalogu): Array<{ pole: string; naglowek: string }> {
  if (!Array.isArray(zmienna.example) || zmienna.example.length === 0) return [];
  const przyklad = zmienna.example[0];
  if (przyklad !== null && typeof przyklad === 'object') {
    return Object.keys(przyklad as Record<string, unknown>)
      .slice(0, KOLUMN_MAX)
      .map((pole) => ({ pole, naglowek: pole }));
  }
  // LISTA SKALARÓW (`['gluten', 'sezam']`) to jedna kolumna, a nie brak
  // tabeli. Interpreter opakowuje taką pozycję w `{ wartosc: poz }` —
  // dokładnie po to, żeby `{pole:'wartosc'}` działało bez osobnego wariantu
  // źródła — więc kolumna nazywa się `wartosc`, a nagłówek bierze nazwę
  // zmiennej, bo „wartosc" nad kolumną alergenów nic nikomu nie mówi.
  return [{ pole: 'wartosc', naglowek: zmienna.name }];
}

/**
 * Zestaw węzłów tworzących TABELĘ: nagłówek, linia i powtórzenie po wierszach.
 *
 * Tabela nie jest osobnym rodzajem węzła i nie ma nim być. Gramatyka E0 zna
 * `repeat` ze źródłem `lista`, a to jest dokładnie tabela: nagłówek to teksty,
 * linia to linia, a wiersze to powtórzenie wiersza szablonowego. Dołożenie
 * `kind: 'table'` znaczyłoby nowy węzeł w IR, nowa gałąź w CZTERECH enkoderach
 * (ZPL/EPL/TSPL/PDF) i nowy kształt w walidatorze — po to, żeby wyrazić coś,
 * co gramatyka już wyraża.
 *
 * Wiersz szablonowy ma `yMm` LICZONE OD POWTÓRZENIA (offset nakłada
 * `obrysyUkladu`), a `xMm` bezwzględne — dlatego kolumny dostają x od zestawu,
 * a y zerowe z korektą na linię bazową.
 */
export function wezlyTabeli(
  zmienne: ZmiennaKatalogu[],
  xMm: number,
  yMm: number,
  szerokoscMm: number
): LayoutNode[] {
  const lista = zmienne.find((z) => z.kind === 'list');
  const kolumny = lista ? kolumnyListy(lista) : [];
  const naglowki = kolumny.length > 0 ? kolumny.map((k) => k.naglowek) : ['Nazwa', 'Ilość'];
  const szerKolumny = Math.max(8, Math.round((szerokoscMm / naglowki.length) * 10) / 10);
  const WIERSZ_MM = 4;
  const PODSTAWA_MM = 2.8;

  const naglowek: LayoutNode[] = naglowki.map((tytul, i) => ({
    id: nowyId(),
    kind: 'text',
    nazwa: `Nagłówek: ${tytul}`,
    xMm: xMm + i * szerKolumny,
    yMm: yMm + PODSTAWA_MM,
    tekst: [{ lit: tytul }],
    sizePt: 7,
    bold: true,
  }));

  const linia: LayoutNode = {
    id: nowyId(),
    kind: 'line',
    nazwa: 'Linia nagłówka',
    xMm,
    yMm: yMm + WIERSZ_MM,
    x2Mm: xMm + naglowki.length * szerKolumny,
    y2Mm: yMm + WIERSZ_MM,
    thicknessMm: 0.3,
  };

  const komorka = (i: number, tresc: Token): LayoutNode => ({
    id: nowyId(),
    kind: 'text',
    xMm: xMm + i * szerKolumny,
    yMm: PODSTAWA_MM,
    tekst: [tresc],
    sizePt: 7,
  });

  const powtorzenie: LayoutNode =
    lista && kolumny.length > 0
      ? {
          id: nowyId(),
          kind: 'repeat',
          nazwa: `Wiersze: ${lista.name}`,
          yMm: yMm + WIERSZ_MM + 1,
          stepMm: WIERSZ_MM,
          zrodlo: {
            rodzaj: 'lista',
            var: lista.name,
            maxWierszy: 8,
            wiersz: kolumny.map((k, i) => komorka(i, { pole: k.pole })),
          },
        }
      : {
          // Wzór bez zmiennej listowej dostaje tabelę STATYCZNĄ — dwa wiersze
          // literałów do wypełnienia ręcznie. To jest uczciwa odpowiedź:
          // powtórzenie po nieistniejącej zmiennej byłoby układem, którego
          // walidator nie przyjmie, a edytor pokazałby je jako gotowe.
          id: nowyId(),
          kind: 'repeat',
          nazwa: 'Wiersze tabeli',
          yMm: yMm + WIERSZ_MM + 1,
          stepMm: WIERSZ_MM,
          zrodlo: {
            rodzaj: 'literalne',
            wiersze: [0, 1].map((w) =>
              naglowki.map((_, i) => komorka(i, { lit: `wiersz ${w + 1}` }))
            ),
          },
        };

  return [...naglowek, linia, powtorzenie];
}

/**
 * Tekst z chwilą wydruku — data, godzina albo jedno i drugie.
 *
 * Osobna funkcja, a nie `nowyWezel('text')` z ręcznym podmienieniem tokena,
 * bo to jest odrębna czynność w pasku narzędzi: „wstaw datę" wykonuje się
 * inaczej niż „wstaw tekst i przełącz mu rodzaj fragmentu na chwilę".
 * Nazwa warstwy bierze się z kształtu, żeby lista warstw nie mówiła „Tekst"
 * o polu, którego treści nikt nie wpisywał.
 */
export function wezelChwili(ksztalt: KsztaltTeraz, xMm: number, yMm: number): LayoutNode {
  return {
    id: nowyId(),
    kind: 'text',
    nazwa: NAZWA_KSZTALTU_TERAZ[ksztalt],
    xMm,
    yMm,
    tekst: [{ teraz: ksztalt }],
    sizePt: 8,
  };
}

/**
 * DOMYŚLNY ROZMIAR PISMA — 8 pt, i to nie jest wybór edytora.
 *
 * `deklaracjeWezla` (facsimile), enkodery ZPL/EPL/TSPL i `obrysWezla` czytają
 * `node.sizePt ?? 8`. Płótno i panel miały własne `?? 10`, więc tekst ze wzoru
 * katalogowego (wzory nie zapisują `sizePt`, a interpreter przepuszcza
 * `undefined`) rysował się o ćwierć większy, niż wychodził z drukarki.
 */
export const DOMYSLNY_ROZMIAR_PT = 8;

/**
 * Pudełko węzła tekstowego na płótnie — w pikselach, ale policzone
 * z `obrysWezla`, a nie z `node.xMm/yMm` na piechotę.
 *
 * DLACZEGO PRZEZ OBRYS. Ten prostokąt JEST uchwytem: łapie się go myszą i to
 * on pokazuje zaznaczenie. Obrys jest tym, co mierzy walidator („blok wyjeżdża
 * poza etykietę") i czym przycinamy przeciąganie. Gdy liczą osobno, uchwyt
 * stoi gdzie indziej niż blok — dokładnie ta wada, przed którą broni nagłówek
 * `obrys.ts` („uchwyt stałby gdzie indziej niż blok").
 *
 * Rozjazd BYŁ i miał wysokość wiersza: płótno odejmowało od `top` wysokość
 * wiersza, tłumacząc to linią bazową. Punktem odniesienia tekstu jest jednak
 * GÓRNA krawędź — tak liczy `deklaracjeWezla` (`top: mm(yMm)`), tak liczy
 * `^FO` w ZPL i tak liczy `obrysWezla`. Każdy napis na płótnie stał więc
 * o wiersz wyżej, niż się drukuje.
 *
 * Szerokość bierzemy TYLKO wtedy, gdy obrys jest jej pewny, czyli gdy węzeł ma
 * `maxWidthMm`. Bez bloku obrys szacuje szerokość metryką fontu; narzucenie
 * jej pudełku ucinałoby napis na ekranie w miejscu, w którym drukarka go nie
 * utnie.
 */
export function pudelkoTekstu(
  node: Extract<LayoutNode, { kind: 'text' }>,
  skala: number
): { left: number; top: number; width: number | undefined; fontSize: number } {
  const o = obrysWezla(node);
  return {
    left: o.xMm * skala,
    top: o.yMm * skala,
    width: o.szacowanaSzerokosc ? undefined : o.widthMm * skala,
    fontSize: ptNaMm(node.sizePt ?? DOMYSLNY_ROZMIAR_PT) * skala,
  };
}
